import { authGetRequest } from "./authClient";

export type SafetyReportApiItem = {
  id: string;
  clientId: string;
  applicationId: string;
  safetyReport: string;
  createdAt: string;
};

export type SafetyReportsApiResponse = {
  success?: boolean;
  message?: string;
  data?: {
    items?: SafetyReportApiItem[];
    pagination?: {
      page?: number;
      size?: number;
      total?: number;
    };
  };
};

export type SafetyReportRow = {
  id: string;
  clientId: string;
  applicationId: string;
  type: string;
  fileName: string;
  createdAt: string;
  safetyReport: string;
};

export type FetchSafetyReportsParams = {
  clientId: string;
  applicationId: string;
  from: string;
  to: string;
  page?: number;
  pageSize?: number;
};

export function getSafetyReportDateRange(days: number): { from: string; to: string } {
  const to = new Date();
  to.setUTCHours(23, 59, 59, 999);

  const from = new Date();
  from.setUTCDate(from.getUTCDate() - days);
  from.setUTCHours(0, 0, 0, 0);

  return { from: from.toISOString(), to: to.toISOString() };
}

export async function fetchSafetyReports(params: FetchSafetyReportsParams) {
  return authGetRequest<SafetyReportsApiResponse>("/safety-report", {
    clientId: params.clientId,
    applicationId: params.applicationId,
    from: params.from,
    to: params.to,
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 50,
  });
}

export function fileNameFromPdfUrl(url: string): string {
  try {
    const name = new URL(url).pathname.split("/").pop() ?? "";
    return name || "report.pdf";
  } catch {
    return "report.pdf";
  }
}

/** Human-readable label from PDF filename (e.g. daily-round-may26.pdf → Daily Round May26). */
export function reportLabelFromPdfUrl(url: string): string {
  const fileName = fileNameFromPdfUrl(url);
  const base = fileName.replace(/\.pdf$/i, "");
  if (!base) return "Safety Report";
  return base
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function mapSafetyReportItem(item: SafetyReportApiItem): SafetyReportRow {
  const safetyReport = String(item.safetyReport ?? "").trim();
  return {
    id: String(item.id ?? "").trim(),
    clientId: String(item.clientId ?? "").trim(),
    applicationId: String(item.applicationId ?? "").trim(),
    type: reportLabelFromPdfUrl(safetyReport),
    fileName: fileNameFromPdfUrl(safetyReport),
    createdAt: String(item.createdAt ?? "").trim(),
    safetyReport,
  };
}

export function parseSafetyReportsResponse(payload: unknown): {
  reports: SafetyReportRow[];
  total: number;
  page: number;
  pageSize: number;
} {
  const root = (payload ?? {}) as SafetyReportsApiResponse;
  const data = root.data;
  const rawItems = data?.items;
  const items = Array.isArray(rawItems) ? rawItems : [];
  const pagination = data?.pagination;

  const reports = items
    .filter((item) => item?.id && item?.safetyReport)
    .map(mapSafetyReportItem);

  return {
    reports,
    total: pagination?.total ?? reports.length,
    page: pagination?.page ?? 1,
    pageSize: pagination?.size ?? reports.length,
  };
}

export function downloadSafetyReport(url: string, fileName?: string) {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = fileName ?? fileNameFromPdfUrl(url);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
